import { differenceInDays, parseISO, startOfDay } from 'date-fns';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import SectionHeroCard from '@/components/SectionHeroCard';

type HealthReminderStatRow = {
  due_date: string | null;
};

export default function HealthHeroCard({
  action,
}: {
  action?: ReactNode;
}) {
  const { user } = useApp();
  const [reminders, setReminders] = useState<HealthReminderStatRow[] | null>(null);

  useEffect(() => {
    if (!user) {
      setReminders(null);
      return;
    }

    async function fetchReminderStats() {
      const { data: reminderRows } = await supabase
        .from('health_reminders')
        .select('due_date')
        .eq('user_id', user.id)
        .is('completed_at', null);

      setReminders((reminderRows as HealthReminderStatRow[]) || []);
    }

    void fetchReminderStats();

    const handleRefresh = () => {
      void fetchReminderStats();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [user]);

  const stats = useMemo(() => {
    if (reminders === null) {
      return [
        { label: 'Overdue', value: '-', tone: 'default' },
        { label: 'Due soon', value: '-', tone: 'default' },
        { label: 'Tracked', value: '-', tone: 'default' },
      ] as const;
    }

    const today = startOfDay(new Date());
    const overdueCount = reminders.filter((item) => {
      if (!item.due_date) return false;
      return differenceInDays(parseISO(item.due_date), today) < 0;
    }).length;
    const dueSoonCount = reminders.filter((item) => {
      if (!item.due_date) return false;
      const daysUntil = differenceInDays(parseISO(item.due_date), today);
      return daysUntil >= 0 && daysUntil <= 30;
    }).length;

    return [
      {
        label: 'Overdue',
        value: String(overdueCount),
        tone: overdueCount > 0 ? 'danger' : 'success',
      },
      {
        label: 'Due soon',
        value: String(dueSoonCount),
        tone: dueSoonCount > 0 ? 'accent' : 'default',
      },
      {
        label: 'Tracked',
        value: String(reminders.length),
        tone: reminders.length > 0 ? 'success' : 'default',
      },
    ] as const;
  }, [reminders]);

  return (
    <SectionHeroCard
      title="My Health"
      subtitle="Stay on top of my health — reminders, wellbeing, and my NHS"
      action={action}
      stats={[...stats]}
    />
  );
}
